import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Crown, ArrowRight, Flame } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionSuccess = () => {
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [subscription, setSubscription] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 15;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      toast.error('Payment verification timed out. Please contact support.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/subscription/status/${sessionId}`, { headers });
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setSubscription(data);
        trackEvent('purchase', {
          transaction_id: sessionId,
          currency: data.currency || 'USD',
          value: data.amount_total ? Number(data.amount_total) / 100 : undefined,
          items: [{ item_id: data.tier_id || 'subscription', item_name: data.tier_name || 'Subscription' }]
        });
        toast.success('Payment successful!');
        return;
      } else if (data.status === 'expired' || data.status === 'failed') {
        setStatus('error');
        trackEvent('purchase_failed', { transaction_id: sessionId, reason: data.status });
        toast.error('Payment failed. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      if (attempts >= maxAttempts - 1) {
        setStatus('error');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    }
  };

  if (status === 'verifying') {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loader2 className="w-20 h-20 text-orange-500 animate-spin mx-auto mb-6" />
          <h2 className="text-3xl font-heading font-black text-white uppercase mb-2">Verifying Payment</h2>
          <p className="text-zinc-500">Please wait while we confirm your subscription...</p>
        </motion.div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 bg-red-500/20 border-2 border-red-500/50 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">✕</span>
          </div>
          <h2 className="text-3xl font-heading font-black text-white uppercase mb-2">Something Went Wrong</h2>
          <p className="text-zinc-500 mb-6">We couldn't verify your payment. Please try again or contact support.</p>
          <div className="flex gap-3 justify-center">
            <Button
              variant="outline"
              onClick={() => navigate('/subscription')}
              className="border-zinc-700 text-zinc-400"
            >
              Try Again
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-orange-600 hover:bg-orange-500 text-white"
            >
              Go to Dashboard
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-lg w-full"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", delay: 0.2 }}
          className="w-24 h-24 bg-emerald-500/20 border-2 border-emerald-500/50 rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <Check className="w-12 h-12 text-emerald-500" />
        </motion.div>

        <h1 className="text-4xl md:text-5xl font-heading font-black text-white uppercase mb-2">
          Welcome to{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">
            {subscription?.tier?.toUpperCase() || 'PRO'}
          </span>
        </h1>
        <p className="text-zinc-400 mb-8">Your subscription is now active. Let's break the cycle!</p>

        <Card className="bg-zinc-900/80 border border-zinc-800 mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Crown className="w-8 h-8 text-amber-500" />
                <div className="text-left">
                  <p className="text-sm text-zinc-500">Your Plan</p>
                  <p className="text-xl font-heading font-bold text-white uppercase">{subscription?.tier || 'Premium'}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-zinc-500">Amount</p>
                <p className="text-xl font-heading font-bold text-emerald-500">${subscription?.amount || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-3">
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              onClick={() => navigate('/grind')}
              className="w-full bg-orange-600 hover:bg-orange-500 text-white font-black uppercase h-14 text-lg"
            >
              <Flame className="w-5 h-5 mr-2" />
              Start Training
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="w-full text-zinc-500 hover:text-white"
          >
            Go to Dashboard
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccess;
